import Highcharts from 'highcharts';
import Select from './select.js';
import GroupSelect from './group_select.js';
import Utils from './project_charts_utils.js';
import Spinnerable from './spinnerable.js';

const ColumnChart = {
  inputRenderers: ['renderXAxisSelect', 'renderIsCountedSelect', 'renderSelect'],
  groupings: ['', 'sectors', 'status'],

  init(containerId) {
    this.containerId = containerId;
    this.container = $(`#${this.containerId}`);
    this.selectedOption = this.groupings[0];
    this.selectedXAxis = 'years';
    this._spinner = this.container.parent().find('.spinner');
    this._isCounted = true;
    this._rendered = false;
    this.listenToSideBarToggleEvent();
    this.listenToWindowResize();
    return this;
  },

  listenToSideBarToggleEvent() {
    $(document).on('sidebar-toggled', _.bind(this.updateWidth, this));
  },

  listenToWindowResize() {
    $(window).on('resize', _.bind(this.updateWidth, this));
  },

  updateWidth() {
    this.chart.update({ chart: { width: this.container.width() } });
  },

  prepareChart(entries) {
    if (!this._rendered) {
      for (let f of this.inputRenderers) {
        this[f]();
      }
      this._renderChartContainer();
    }
    this.entries = entries;
    this.groups = this.generateGroups(this.entries);
    this.columns = this.generateColumns(this.groups);
  },

  renderChart() {
    this.chartContainer.html('');
    if (this.chartContainer.length === 0) { return; }
    const chart = Highcharts.chart(this.chartContainer[0], {
      title: {
        text: this.title,
        align: 'left'
      },
      chart: {
        type: 'column',
        inverted: this.inverted,
        panning: true,
        zoomType: 'xy',
        styledMode: false,
        events: {
          load: this.hideSpinner
        }
      },
      xAxis: {
        categories: this.columnsTranslation(this.columns),
        crosshair: true
      },
      yAxis: {
        title: {
          enabled: false
        }
      },
      legend: {
        enabled: this.isGrouped()
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: true,
            inside: this.isGrouped(),
            formatter: this._valueFormatter,
            style: {
              color: '#3b434c'
            }
          }
        }
      },
      series: this.series(),
      exporting: {
        enabled: true
      },
      credits: {
        enabled: false
      },
      tooltip: {
        formatter: this._tooltipFormatter,
        shared: this.isGrouped(),
        useHTML: true
      }
    }
    );
    if (!this._rendered) {
      this.container.css('height', `${this.container.height()}px`); // preserve space
      this._rendered = true;
    }
    return chart;
  },

  render(entries) {
    this.prepareChart(entries);
    this.chart = this.renderChart();
  },

  onSelectChange(e) {
    this.selectedOption = e.target.value;
  },

  onXAxisChange(e) {
    this.selectedXAxis = e.target.value;
  },

  onIsCountedChange(e) {
    this._isCounted = e.target.value === 'number';
  },

  renderSelect(_afterCallback) {
    const afterGroupCallback = () => {
      this.selectedOption = this.isGrouped() ? '' : 'sectors';
      this.render(this.entries);
    };

    const afterSelectCallback = function () {
      this.render(this.entries);
    };

    GroupSelect.init(this.containerId, this.groupings, this.handleInputChange(this.onSelectChange, afterSelectCallback), afterGroupCallback, 'group').render();
  },

  renderXAxisSelect(afterCallback) {
    afterCallback = function (e) {
      this.render(this.entries);
    };

    Select.init(this.containerId, ['years', 'quarters'], this.handleInputChange(this.onXAxisChange, afterCallback), 'x_axis').render();
  },

  isGrouped() {
    return this.selectedOption !== '';
  },

  renderIsCountedSelect(afterCallback) {
    afterCallback = function () {
      this.render(this.entries);
    };

    Select.init(this.containerId, ['number', 'value'], this.handleInputChange(this.onIsCountedChange, afterCallback), 'y_axis').render();
  },

  series() {
    let key;
    const types = _.keys(this.countGroup(this.entries, this.selectedOption, this._isCounted)).sort();
    const seriesTmp = _.object(types.map((type) => {
      return [type, _.map(new Array(this.columns.length), () => 0)]
    }));
    for (let index = 0; index < this.columns.length; index++) {
      const column = this.columns[index];
      const object = this.countGroup(this.groups[column], this.selectedOption, this._isCounted);
      for (key in object) {
        const count = object[key];
        seriesTmp[key][index] = count;
      }
    }

    const isGrouped = this.isGrouped();
    return _.map(seriesTmp, (value, key) => {
      return {
        name: this._translate(key),
        data: value,
        isCounted: this._isCounted,
        isGrouped: isGrouped
      }
    })
  },

  _renderChartContainer() {
    this.chartContainer = $('<div>').appendTo(this.container);
  },

  _translate(key) {
    if (this.selectedOption === 'status') {
      return I18n.t(`enumerize.project.short_status.${key}`);
    } else {
      return key;
    }
  },

  _valueFormatter() {
    if (this.series.options.isCounted) {
      return this.y;
    } else {
      return `<b>${convertNbToString(this.y)}</b>`;
    }
  },

  _tooltipFormatter(tooltip) {
    const singleValueFormatter = function (isCounted, point) {
      if (isCounted) {
        return `<b>${point.x}:</b> ${point.y}`;
      } else {
        return `<b>${point.x}:</b> ${convertNbToString(point.y)}`;
      }
    };

    if (this.series) {
      return singleValueFormatter(this.series.options.isCounted, this);
    } else {
      let element = `<span style='font-size: 12px'><b>${this.x}</b></span><br/>`;
      _.each(this.points, point => element += `<span class='highcharts-color-${point.colorIndex}'>●</span>  ${singleValueFormatter(point.series.options.isCounted, { x: point.series.name, y: point.y })}<br/>`);
      return element;
    }
  },

  // Placeholder functions to overwrite
  columnsTranslation(columns) { return columns; },
  groups(entries) { return Object.create(null); },
  columns(groups) { return []; }
};

_.defaults(ColumnChart, Utils);
_.defaults(ColumnChart, Spinnerable);

export default ColumnChart;